/**
 * Automation Filters
 *
 * UI for configuring WHO an automation applies to using client filters:
 * - Group membership
 * - Location assignment
 * - Tag presence
 * - Lifecycle stage
 * - Subscription status
 * - Purchase history
 *
 * Filters can be combined with AND/OR logic and nested for complex conditions.
 */

import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';

import { FilterSet } from './FilterSet';
import { FilterGroup, FilterNode } from './types';

export type FilterValue = {
  type: FilterNode['type'];
  value: string;
};

interface AutomationFiltersProps {
  value?: FilterGroup;
  onChange: (v?: FilterGroup) => void;
  allowedFilterTypes?: ReadonlyArray<FilterNode['type']>;
}

export const AutomationFilters: FC<AutomationFiltersProps> = ({
  value,
  onChange,
  allowedFilterTypes,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'automationFilters',
  });

  const [editing, setEditing] = useState(false);
  const [curType, setCurType] = useState<FilterValue['type'] | ''>('');
  const [curCondition, setCurCondition] = useState<
    FilterGroup['condition'] | ''
  >('');

  const hasFilters = value && value.filters && value.filters.length > 0;

  const canAdd = curType && (curCondition || !hasFilters);

  const handleAdd = () => {
    if (!curType) return;
    const { filters, condition } = value!;
    const newNode: FilterNode = { type: curType, value: null, meta: {} };

    if (!hasFilters) {
      // first filter
      onChange({ condition: 'AND', filters: [newNode] });
    } else if (filters.length === 1) {
      // second filter
      onChange({
        condition: curCondition as FilterGroup['condition'],
        filters: [...filters, newNode],
      });
    } else {
      if (curCondition === condition) {
        onChange({ ...value!, filters: [...filters, newNode] });
      } else {
        const subFilters = filters.find((f) => {
          return 'condition' in f && f.condition == curCondition;
        });
        if (subFilters && 'filters' in subFilters) {
          const updatedFilters = filters.map((f) => {
            if ('condition' in f && f.condition === curCondition) {
              return {
                ...f,
                filters: [...(f.filters || []), newNode],
              };
            }
            return f;
          });
          onChange({ ...value!, filters: updatedFilters });
        } else {
          const newFilters = [
            ...filters.slice(0, -1),
            {
              condition: curCondition as FilterGroup['condition'],
              filters: [filters[filters.length - 1], newNode],
            },
          ];

          onChange({
            ...value,
            filters: newFilters,
          });
        }
      }
    }

    setCurType('');
    setCurCondition('');
    setEditing(false);
  };

  const filterTypeOptions = [
    { value: '', label: t('selectFilterType') },
    { value: 'group', label: t('filterType.group') },
    { value: 'location', label: t('filterType.location') },
    { value: 'tag', label: t('filterType.tag') },
    { value: 'lifecycle', label: t('filterType.lifecycle') },
    { value: 'subscription', label: t('filterType.subscription') },
    { value: 'purchase', label: t('filterType.purchase') },
  ].filter(
    (option) =>
      !option.value ||
      !allowedFilterTypes ||
      allowedFilterTypes.includes(option.value as FilterNode['type'])
  );

  const conditionOptions = [
    { value: '', label: t('selectCondition') },
    { value: 'AND', label: t('and') },
    { value: 'OR', label: t('or') },
  ];

  return (
    <Box>
      {hasFilters ? (
        <>
          <Typography variant="body2">{t('usersWho')}</Typography>
          <FilterSet
            filter={value}
            root={value}
            onChangeRoot={(v) => onChange(v)}
          />
        </>
      ) : (
        <Typography variant="body2">{t('allAccounts')}</Typography>
      )}
      {editing ? (
        <Box mt={1}>
          <Typography variant="body2">{t('selectFilterType')}</Typography>
          <TextField
            value={curType}
            onChange={(e) => setCurType(e.target.value as FilterValue['type'])}
            options={filterTypeOptions}
            sx={{ minWidth: 180 }}
          />

          {hasFilters && (
            <>
              <Typography variant="body2" mt={1}>
                {t('connectWith')}
              </Typography>
              <TextField
                value={curCondition}
                onChange={(e) =>
                  setCurCondition(e.target.value as FilterGroup['condition'])
                }
                options={conditionOptions}
                sx={{ minWidth: 220 }}
              />
            </>
          )}

          <Button
            variant="prominent"
            disabled={!canAdd}
            onClick={handleAdd}
            sx={{ ml: 1, mt: 1 }}
          >
            {t('addFilter')}
          </Button>
        </Box>
      ) : (
        <Button
          variant="bordered"
          onClick={() => setEditing(true)}
          sx={{ mt: 1 }}
        >
          {t('addFilter')}
        </Button>
      )}
    </Box>
  );
};

// Backwards compatibility alias
/** @deprecated Use AutomationFilters instead */
export const ReminderFilters = AutomationFilters;
